import { useState } from 'react';

import { Link } from 'react-router-dom';

import { useCreateOrganisation } from '@/api/mutations/useCreateOrganisation';
import {
  ORGANISATION_DOMAIN_REGEX,
  ORGANISATION_NAME_MAX_LENGTH,
  OrganisationCreateResponseDto,
} from '@/api/types/backend-dto-types';
import { CopyIconButton } from '@/new/components/copy-icon-button';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/new/components/shadcn/dialog';
import { Input } from '@/new/components/shadcn/input';
import { Label } from '@/new/components/shadcn/label';
import { Switch } from '@/new/components/shadcn/switch';

type Step = 'form' | 'confirm' | 'result';

/** Fixed PropelAuth settings applied by the backend; shown so the admin knows what they are not choosing. */
const FIXED_SETTINGS_COPY = 'No user limit. Auto-join by email domain off. Members are not restricted to the domain.';

/**
 * Creates an organisation in PropelAuth and Wexler from the admin list page.
 *
 * Three steps: collect name, domain and the SSO toggle; confirm (the button waits on
 * PropelAuth); then show the new org id and, if SSO was requested, the setup link to
 * send to the customer's IT admin. The link needs the new org id, which is why it can
 * only appear after creation rather than alongside the toggle.
 */
export function CreateOrganisationDialog() {
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState<Step>('form');
  const [name, setName] = useState('');
  const [domain, setDomain] = useState('');
  const [enableSso, setEnableSso] = useState(false);
  const [result, setResult] = useState<OrganisationCreateResponseDto | null>(null);

  const { mutate: createOrganisation, isLoading } = useCreateOrganisation();

  const trimmedName = name.trim();
  const normalisedDomain = domain.trim().toLowerCase();
  const isNameValid = trimmedName !== '' && trimmedName.length <= ORGANISATION_NAME_MAX_LENGTH;
  const isDomainValid = ORGANISATION_DOMAIN_REGEX.test(normalisedDomain);
  const showDomainHint = domain !== '' && !isDomainValid;
  const isFormValid = isNameValid && isDomainValid;

  const reset = () => {
    setStep('form');
    setName('');
    setDomain('');
    setEnableSso(false);
    setResult(null);
  };

  const handleOpenChange = (open: boolean) => {
    // Never let the dialog be dismissed while PropelAuth is being called.
    if (!open && isLoading) return;
    setIsOpen(open);
    if (!open) reset();
  };

  const handleConfirm = () => {
    if (!isFormValid) return;
    createOrganisation(
      { name: trimmedName, domain: normalisedDomain, enableSso },
      {
        onSuccess: (created) => {
          setResult(created);
          setStep('result');
        },
      },
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button>
          <Icon className="lucide--plus" />
          Create organisation
        </Button>
      </DialogTrigger>
      <DialogContent>
        {step === 'form' && (
          <>
            <DialogHeader>
              <DialogTitle>Create organisation</DialogTitle>
              <DialogDescription>
                Creates the organisation in PropelAuth and Wexler. Users join once they are invited or sign in via SSO.
              </DialogDescription>
            </DialogHeader>

            <div className="mt-4 grid gap-4">
              <div className="grid gap-1.5">
                <Label htmlFor="create-org-name">Organisation name</Label>
                <Input
                  id="create-org-name"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  placeholder="Acme Legal LLP"
                  maxLength={ORGANISATION_NAME_MAX_LENGTH}
                  autoFocus
                />
              </div>

              <div className="grid gap-1.5">
                <div>
                  <Label htmlFor="create-org-domain" className="mb-1">
                    Email domain
                  </Label>
                  <p className={showDomainHint ? 'text-red-600' : 'text-gray-500'}>
                    {showDomainHint
                      ? 'Enter a bare domain such as acme.com, without https:// or a path.'
                      : 'The domain their staff email addresses use. Required for Enterprise SSO.'}
                  </p>
                </div>
                <Input
                  id="create-org-domain"
                  value={domain}
                  onChange={(event) => setDomain(event.target.value)}
                  placeholder="acme.com"
                  className="font-mono"
                  autoCapitalize="none"
                  spellCheck={false}
                />
              </div>

              <div className="flex items-center gap-5 p-3">
                <Switch id="create-org-sso" checked={enableSso} onCheckedChange={setEnableSso} className="mt-0.5" />
                <div className="space-y-0.5">
                  <Label htmlFor="create-org-sso" className="mb-1 font-medium text-gray-700">
                    Enable Enterprise SSO
                  </Label>
                  <p className="text-gray-500">
                    Allows this organisation to set up SAML/OIDC and generates a setup link for their IT admin.
                  </p>
                </div>
              </div>
            </div>

            <DialogFooter className="mt-6">
              <Button variant="ghost" onClick={() => handleOpenChange(false)}>
                Cancel
              </Button>
              <Button variant="primary" onClick={() => setStep('confirm')} disabled={!isFormValid}>
                Next
              </Button>
            </DialogFooter>
          </>
        )}

        {step === 'confirm' && (
          <>
            <DialogHeader>
              <DialogTitle>Confirm new organisation</DialogTitle>
              <DialogDescription>
                This creates the organisation in PropelAuth immediately. It cannot be undone from Wexler.
                {enableSso && ' The SSO setup link will be provided after creation.'}
              </DialogDescription>
            </DialogHeader>

            <dl className="mx-auto mt-4 grid w-fit max-w-full grid-cols-[auto_minmax(0,1fr)] gap-x-6 gap-y-1.5 text-sm">
              <dt className="text-right text-gray-500">Name</dt>
              <dd className="min-w-0 font-medium break-words text-gray-800">{trimmedName}</dd>
              <dt className="text-right text-gray-500">Email domain</dt>
              <dd className="min-w-0 font-mono break-all text-gray-800">{normalisedDomain}</dd>
              <dt className="text-right text-gray-500">Enterprise SSO</dt>
              <dd className="min-w-0 break-words text-gray-800">
                {enableSso ? 'On, setup link will be generated' : 'Off'}
              </dd>
            </dl>
            <p className="mt-6 text-sm text-gray-500">{FIXED_SETTINGS_COPY}</p>

            <DialogFooter className="mt-6">
              <Button variant="ghost" onClick={() => setStep('form')} disabled={isLoading}>
                Back
              </Button>
              <Button variant="primary" onClick={handleConfirm} disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Icon className="lucide--loader animate-spin ease-in-out" />
                    Creating…
                  </>
                ) : enableSso ? (
                  'Create organisation and get SSO link'
                ) : (
                  'Create organisation'
                )}
              </Button>
            </DialogFooter>
          </>
        )}

        {step === 'result' && result && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Icon className="lucide--circle-check text-teal-600" />
                Organisation created
              </DialogTitle>
              <DialogDescription>
                {result.name} now exists in PropelAuth and Wexler. The KMS key and default roles finish provisioning in
                the background.
              </DialogDescription>
            </DialogHeader>

            <div className="mt-4 grid gap-4">
              <div className="grid gap-1.5">
                <Label htmlFor="created-org-id">Organisation id</Label>
                <div className="flex items-center gap-1">
                  <Input id="created-org-id" value={result.id} readOnly className="font-mono" />
                  <CopyIconButton getText={() => result.id} aria-label="Copy organisation id" />
                </div>
              </div>

              {result.ssoConnectionUrl && (
                <div className="grid gap-1.5">
                  <div>
                    <Label htmlFor="created-org-sso-link" className="mb-1">
                      SSO setup link
                    </Label>
                    <p className="text-gray-500">
                      Send this to the customer&apos;s IT admin. It opens the PropelAuth SAML/OIDC setup page without a
                      Wexler login and expires, so copy it now.
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    <Input
                      id="created-org-sso-link"
                      value={result.ssoConnectionUrl}
                      readOnly
                      className="font-mono"
                      onFocus={(event) => event.target.select()}
                    />
                    <CopyIconButton getText={() => result.ssoConnectionUrl ?? ''} aria-label="Copy SSO setup link" />
                  </div>
                </div>
              )}

              {result.ssoSetupError && (
                <div className="flex gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                  <Icon className="lucide--triangle-alert mt-0.5 shrink-0 text-amber-600" />
                  <p>
                    The organisation was created but SSO setup failed: {result.ssoSetupError}. Enable Enterprise SSO and
                    generate the setup link for this organisation in the PropelAuth dashboard.
                  </p>
                </div>
              )}
            </div>

            <DialogFooter className="mt-6">
              <Button variant="ghost" asChild>
                <Link to={`/admin/organisations/${result.id}`}>Manage organisation</Link>
              </Button>
              <Button variant="primary" onClick={() => handleOpenChange(false)}>
                Done
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
